import { useState } from 'react';
import * as Yup from 'yup';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
// material
import { Typography, Container, Grid, FormLabel, Button, CircularProgress } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
//
import RichTextEditor from 'src/components/RichTextEditor';
import { createLegalPage } from 'src/api/api';
// ----------------------------------------------------------------------

export default function CreatePage() {
  const navigate = useNavigate();
  const { country } = useParams();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Validation Schema for Form
  const PageSchema = Yup.object({
    pageName: Yup.string().required('Page Name is required'),
    pageSlug: Yup.string()
      .required('Page Slug is required')
      .matches(/^\/[a-z0-9-]+$/, 'Slug must start with / and contain only lowercase letters, numbers and hyphens')
  });

  return (
    <Container>
      <Grid container justifyContent="center">
        <Grid item lg={10} md={10} xs={10} sm={10} mx="auto">
          <Card
            sx={{
              minWidth: 275,
              pl: 2,
              pt: 2,
              boxShadow: 1,
              borderRadius: 1,
              border: '1px solid #929eaa6b'
            }}
          >
            <CardContent>
              <Typography variant="h4" gutterBottom>
                Create Page
              </Typography>
              {errorMsg && (
                <Typography color="error" mb={2}>
                  {errorMsg}
                </Typography>
              )}
              <Formik
                initialValues={{
                  pageName: '',
                  pageSlug: '',
                  metaTitle: '',
                  metaDescription: '',
                  canonicalLink: '',
                  country: country,
                  content: ''
                }}
                validationSchema={PageSchema}
                onSubmit={async (values) => {
                  try {
                    setLoading(true);
                    setErrorMsg('');
                    const res = await createLegalPage(values);
                    if (res.success) {
                      navigate('/dashboard/pages');
                    } else {
                      setErrorMsg(res.message || 'Failed to create page. Please try again.');
                    }
                  } catch (error) {
                    console.error('Failed to create page:', error);
                    setErrorMsg(
                      error?.response?.data?.message || 'Something went wrong. Please try again.'
                    );
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {({ values, setFieldValue }) => (
                  <Form>
                    <div className="col spacing-form-title">
                      <FormLabel htmlFor="pageName">Page Name</FormLabel>
                      <Field
                        id="pageName"
                        name="pageName"
                        type="text"
                        placeholder="Enter Page Name"
                        className="input-style"
                      />
                      <ErrorMessage name="pageName" component="div" className="error-color" />
                    </div>
                    <div className="col spacing-form-title">
                      <FormLabel htmlFor="pageSlug">Page Slug</FormLabel>
                      <Field
                        id="pageSlug"
                        name="pageSlug"
                        type="text"
                        placeholder="e.g. /about"
                        className="input-style"
                      />
                      <ErrorMessage name="pageSlug" component="div" className="error-color" />
                      <p className="helper-text-style">
                        Must start with / and use lowercase letters, numbers and hyphens only.
                      </p>
                    </div>
                    <div className="col spacing-form-title">
                      <FormLabel htmlFor="metaTitle">SEO Meta Title</FormLabel>
                      <Field
                        id="metaTitle"
                        name="metaTitle"
                        type="text"
                        placeholder="Enter Meta Title"
                        className="input-style"
                      />
                    </div>

                    <div className="col spacing-form-title">
                      <FormLabel htmlFor="metaDescription">SEO Meta Description</FormLabel>
                      <Field
                        id="metaDescription"
                        name="metaDescription"
                        type="text"
                        placeholder="Write Meta Description"
                        className="input-style"
                      />
                    </div>

                    <div className="col spacing-form-title">
                      <FormLabel htmlFor="canonicalLink">SEO Canonical Link</FormLabel>
                      <Field
                        id="canonicalLink"
                        name="canonicalLink"
                        type="text"
                        placeholder="Enter Canonical Link"
                        className="input-style"
                      />
                    </div>

                    <div className="col spacing-form-title">
                      <FormLabel htmlFor="content"> Content </FormLabel>
                      <RichTextEditor
                        setFieldValue={setFieldValue}
                        name="content"
                        value={values?.content}
                      />
                      <ErrorMessage name="content" component="div" className="error-color" />
                    </div>

                    <Grid container>
                      <Grid item xs={4} sm={4} md={3} lg={2} mt={3}>
                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          type="submit"
                          disabled={loading}
                        >
                          <span style={{ marginRight: 10 }}>Submit</span>
                          {loading && <CircularProgress size={20} />}
                        </Button>
                      </Grid>
                      <Grid item xs={4} sm={4} md={3} lg={2} ml={2} mt={3}>
                        <Button
                          fullWidth
                          size="large"
                          variant="contained"
                          color="error"
                          component={RouterLink}
                          to="/dashboard/pages"
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}